import type { IconName } from '@meterup/atto';
import { getResizeCursor, sizing } from '@meterup/atto';
import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  FLEXI_COLUMNS_ANIMATION_MS,
  FLEXI_COLUMNS_DIVIDER_WIDTH,
  FLEXI_COLUMNS_RESIZE_BOUNDS,
  FlexiColumnsColumn,
  FlexiColumnsColumnBody,
  FlexiColumnsContainer,
  FlexiColumnsDivider,
  FlexiColumnsOverlay,
  FlexiColumnsToggleButton,
} from './FlexiColumns.styles';
import { FlexiColumnsControls } from './FlexiColumnsControls';

export type FlexiColumnsActiveColumn = 'start' | 'end';

export interface FlexiColumnsContextValue {
  setActiveColumn: (pane: FlexiColumnsActiveColumn) => void;
  selectedId: string | null;
  onDeselect?: () => void;
  /** Called by a nested FlexiColumns to report its active column (null on unmount). */
  onNestedActiveColumnChange?: (column: FlexiColumnsActiveColumn | null) => void;
}

const FlexiColumnsContext = createContext<FlexiColumnsContextValue | null>(null);

export function useFlexiColumnsContext() {
  return useContext(FlexiColumnsContext);
}

export interface FlexiColumnContent {
  /** Hides the column and its controls without unmounting content or changing saved preferences. */
  hidden?: boolean;
  collapsible?: boolean;
  icon?: IconName;
  label: string;
  /**
   * Width of the collapsed peek strip in pixels. Defaults to `sizing.primary`. Set to 0 to hide.
   */
  peekWidth?: number;
  width?: number;
  render: ReactNode | ((props: { selectedId: string | null }) => ReactNode);
}

export interface FlexiColumnsResizeBounds {
  min: number;
  max: number;
}

export interface FlexiColumnsProps {
  /**
   * Whether divider controls fade out while the columns are inactive. Defaults to `true`.
   */
  autoHideControls?: boolean;
  /**
   * Content for the start column (list, summary, etc.). Uses `FlexiColumns` context for selection.
   */
  start: FlexiColumnContent;
  /**
   * Renders the end column content for the selected item. Receives `selectedId` for the caller to resolve.
   */
  end: FlexiColumnContent;
  /**
   * When `selectedId` is `null`, which column to show.
   * Defaults to `'start'`. Use `'end'` when the end pane has standalone content (e.g. a form).
   */
  initialColumn?: FlexiColumnsActiveColumn;
  /**
   * In a `wide-narrow` layout, keeps the start column at its full width and positions the end
   * column over it.
   */
  endOverlay?: boolean;
  /**
   * Called when the selection is cleared (e.g. row deselect, delayed for animation).
   * Only used when the container is narrow.
   */
  onDeselect?: () => void;
  /**
   * Called when both panes visibility changes (e.g. collapse/expand on desktop).
   * Use with `useFlexiColumnsStorage` for persistence.
   */
  onBothPanesVisibleChange?: (visible: boolean) => void;
  /**
   * Called when start column width changes (after resize ends).
   * Use with `useFlexiColumnsStorage` for persistence.
   */
  onStartWidthChange?: (width: number) => void;
  /**
   * Column width preset: narrow-wide (start narrow), wide-narrow (end narrow), or even-split.
   * */
  preset: 'narrow-wide' | 'wide-narrow' | 'even-split';
  /**
   * Initial values from persisted storage. When provided, override defaults.
   */
  persistedState?: {
    bothPanesVisible?: boolean;
    startWidth?: number;
  };
  /**
   * Whether the columns are resizeable.
   */
  resizable?: boolean;
  /**
   * Minimum and maximum width of the resizable column in pixels.
   */
  resizeBounds?: FlexiColumnsResizeBounds;
  /**
   * ID of the currently selected item. When set, the end column shows its detail.
   */
  selectedId?: string | null;
}

export function FlexiColumns({
  autoHideControls = true,
  end: endProp,
  endOverlay = false,
  initialColumn = 'start',
  onBothPanesVisibleChange,
  onDeselect,
  onStartWidthChange,
  persistedState,
  preset,
  resizable = true,
  resizeBounds = FLEXI_COLUMNS_RESIZE_BOUNDS,
  selectedId,
  start: startProp,
}: FlexiColumnsProps) {
  const start = { collapsible: startProp.collapsible ?? false, ...startProp };
  const end = { collapsible: endProp.collapsible ?? false, ...endProp };
  const parentContext = useFlexiColumnsContext();
  const location = useLocation();
  const locationRef = useRef(location.pathname + location.search);

  const [nestedActiveColumn, setNestedActiveColumn] = useState<FlexiColumnsActiveColumn | null>(
    null,
  );
  const handleNestedActiveColumnChange = useCallback((column: FlexiColumnsActiveColumn | null) => {
    setNestedActiveColumn(column);
  }, []);
  const columnsContainerRef = useRef<HTMLDivElement>(null);
  const endPaneRef = useRef<HTMLDivElement>(null);
  const startPaneRef = useRef<HTMLDivElement>(null);
  const previousActivePaneRef = useRef<FlexiColumnsActiveColumn>('start');
  const { min: minStartWidth, max: maxStartWidth } = resizeBounds;
  const defaultStartWidth =
    preset === 'even-split' ? (minStartWidth + maxStartWidth) / 2 : start.width ?? end.width ?? 400;
  const initialStartWidth = persistedState?.startWidth ?? defaultStartWidth;
  const initialBothPanesVisible = persistedState?.bothPanesVisible ?? true;
  const [startWidth, setStartWidth] = useState(initialStartWidth);
  const [bothPanesVisible, setBothPanesVisible] = useState(initialBothPanesVisible);

  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  useEffect(() => {
    if (persistedState !== undefined && !isResizingRef.current) {
      setStartWidth(persistedState.startWidth ?? defaultStartWidth);
      setBothPanesVisible(persistedState.bothPanesVisible ?? true);
    }
  }, [persistedState, defaultStartWidth]);

  useEffect(() => {
    if (!resizable) {
      setStartWidth(defaultStartWidth);
    }
  }, [resizable, defaultStartWidth]);

  const resizeStartXRef = useRef<number>(0);
  const resizeStartWidthRef = useRef<number>(defaultStartWidth);

  const [activeColumn, setActiveColumn] = useState<FlexiColumnsActiveColumn>(() =>
    selectedId ? 'end' : initialColumn,
  );
  const hasHiddenColumn = !!(start.hidden || end.hidden);
  const showBothPanes = bothPanesVisible && !hasHiddenColumn;
  let displayedColumn = activeColumn;
  if (start.hidden) displayedColumn = 'end';
  else if (end.hidden) displayedColumn = 'start';

  useEffect(() => {
    if (selectedId) {
      setActiveColumn('end');
      setBothPanesVisible(true);
    } else {
      setActiveColumn(initialColumn);
    }
  }, [selectedId, initialColumn]);

  useEffect(() => {
    if (!parentContext?.onNestedActiveColumnChange) return undefined;
    parentContext.onNestedActiveColumnChange(start.hidden && end.hidden ? null : displayedColumn);
    return () => parentContext.onNestedActiveColumnChange!(null);
  }, [parentContext, displayedColumn, start.hidden, end.hidden]);

  useEffect(() => {
    const currentLocation = location.pathname + location.search;
    const previousLocation = locationRef.current;

    if (
      currentLocation !== previousLocation &&
      activeColumn === 'end' &&
      !selectedId &&
      initialColumn === 'start'
    ) {
      setActiveColumn('start');
    }

    locationRef.current = currentLocation;
  }, [location.pathname, location.search, activeColumn, selectedId, initialColumn]);

  useEffect(() => {
    setBothPanesVisible((prev) => {
      if (!prev) {
        const startCollapsed = activeColumn === 'end';
        const endCollapsed = activeColumn === 'start';
        if ((startCollapsed && !start.collapsible) || (endCollapsed && !end.collapsible)) {
          return true;
        }
      }
      return prev;
    });
  }, [activeColumn, start.collapsible, end.collapsible]);

  const latestStartWidthRef = useRef(startWidth);
  latestStartWidthRef.current = startWidth;

  const clampWidth = useCallback(
    (width: number) => Math.min(maxStartWidth, Math.max(minStartWidth, width)),
    [minStartWidth, maxStartWidth],
  );

  const resizeNarrowColumnSign = preset === 'wide-narrow' ? -1 : 1;

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (!resizable) return;
      e.preventDefault();
      (e.target as Element).setPointerCapture(e.pointerId);
      isResizingRef.current = true;
      setIsResizing(true);
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = startWidth;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - resizeStartXRef.current;
        setStartWidth(clampWidth(resizeStartWidthRef.current + delta * resizeNarrowColumnSign));
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
        isResizingRef.current = false;
        setIsResizing(false);
        onStartWidthChange?.(latestStartWidthRef.current);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
    },
    [resizable, startWidth, clampWidth, onStartWidthChange, resizeNarrowColumnSign],
  );

  const RESIZE_STEP = 20;
  const RESIZE_STEP_LARGE = 60;

  const handleResizeByKeyboard = useCallback(
    (e: React.KeyboardEvent) => {
      if (!resizable) return;
      const isLarge = e.shiftKey;
      const step = isLarge ? RESIZE_STEP_LARGE : RESIZE_STEP;
      let delta = 0;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
      else if (e.key === 'Home') delta = minStartWidth - startWidth;
      else if (e.key === 'End') delta = maxStartWidth - startWidth;
      else return;
      e.preventDefault();
      const newWidth = clampWidth(startWidth + delta * resizeNarrowColumnSign);
      setStartWidth(newWidth);
      onStartWidthChange?.(newWidth);
    },
    [
      resizable,
      startWidth,
      minStartWidth,
      maxStartWidth,
      clampWidth,
      onStartWidthChange,
      resizeNarrowColumnSign,
    ],
  );

  const handleResetWidth = useCallback(() => {
    setStartWidth(defaultStartWidth);
    onStartWidthChange?.(defaultStartWidth);
  }, [defaultStartWidth, onStartWidthChange]);

  const handleShowStart = useCallback(() => {
    setActiveColumn('start');
    setBothPanesVisible(true);
    onBothPanesVisibleChange?.(true);
  }, [onBothPanesVisibleChange]);

  const handleCollapseStart = useCallback(() => {
    if (!start.collapsible) return;
    setBothPanesVisible(false);
    setActiveColumn('end');
    onBothPanesVisibleChange?.(false);
  }, [start.collapsible, onBothPanesVisibleChange]);

  const handleCollapseEnd = useCallback(() => {
    if (!end.collapsible) return;
    setBothPanesVisible(false);
    setActiveColumn('start');
    onBothPanesVisibleChange?.(false);
  }, [end.collapsible, onBothPanesVisibleChange]);

  const handleShowEnd = useCallback(() => {
    setBothPanesVisible(true);
    setActiveColumn('end');
    onBothPanesVisibleChange?.(true);
  }, [onBothPanesVisibleChange]);

  // Escape key: return to start when in end pane
  useEffect(() => {
    if (hasHiddenColumn || activeColumn !== 'end') return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (nestedActiveColumn === 'end') return;
        handleShowStart();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeColumn, handleShowStart, nestedActiveColumn, hasHiddenColumn]);

  // Focus management when pane changes
  useLayoutEffect(() => {
    if (start.hidden && end.hidden) return undefined;
    if (previousActivePaneRef.current === displayedColumn) return undefined;

    previousActivePaneRef.current = displayedColumn;

    if (displayedColumn === 'end') {
      const timer = setTimeout(() => {
        const firstFocusable = endPaneRef.current?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        firstFocusable?.focus({ preventScroll: true });
      }, FLEXI_COLUMNS_ANIMATION_MS);
      return () => clearTimeout(timer);
    }

    if (displayedColumn === 'start') {
      const timer = setTimeout(() => {
        const selectedRow = selectedId
          ? startPaneRef.current?.querySelector<HTMLElement>(
              `[data-item-id="${selectedId}"], [data-row-id="${selectedId}"]`,
            )
          : null;
        const firstRow = startPaneRef.current?.querySelector<HTMLElement>(
          '[data-item-id], [data-row-id]',
        );
        (selectedRow ?? firstRow)?.focus({ preventScroll: true });
      }, FLEXI_COLUMNS_ANIMATION_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [displayedColumn, selectedId, start.hidden, end.hidden]);

  const startPaneVisible = !start.hidden && (showBothPanes || displayedColumn === 'start');
  const endPaneVisible = !end.hidden && (showBothPanes || displayedColumn === 'end');
  const startPaneInteractive = !start.hidden && displayedColumn !== 'start' && !showBothPanes;
  const endPaneInteractive = !end.hidden && displayedColumn !== 'end' && !showBothPanes;
  const hasDivider = showBothPanes;
  const hasControls = resizable || start.collapsible || end.collapsible;
  const effectiveStartWidth = hasDivider && resizable ? startWidth : defaultStartWidth;
  const resizeCursor = getResizeCursor({
    axis: 'x',
    value: effectiveStartWidth,
    min: minStartWidth,
    max: maxStartWidth,
    reverse: resizeNarrowColumnSign === -1,
  });

  const weightSum = minStartWidth + maxStartWidth;
  const evenSplitWeight = Math.min(maxStartWidth, Math.max(minStartWidth, effectiveStartWidth));

  /**
   * Width each column occupies while both are showing, mirroring the grid tracks in
   * `FlexiColumnsContainer`. A collapsed column pins its body to this so its content keeps the
   * structure it had rather than reflowing into the peek strip. `100cqw` resolves against
   * `FlexiColumnsContainer`, the nearest inline-size container above either body.
   *
   * `--flexi-start-width` can't stand in for the resizable case: it follows `effectiveStartWidth`,
   * which falls back to the default the moment a pane collapses.
   */
  const expandedWidths = useMemo(() => {
    const full = '100cqw';
    const resizedStart = clampWidth(startWidth);

    if (endOverlay) {
      const overlayWidth = resizable ? resizedStart : end.width ?? defaultStartWidth;
      return { start: full, end: `min(${overlayWidth}px, ${full})` };
    }

    if (preset === 'even-split') {
      const startFraction = resizable ? resizedStart / weightSum : 0.5;
      return {
        start: `calc((${full} - ${FLEXI_COLUMNS_DIVIDER_WIDTH}) * ${startFraction})`,
        end: `calc((${full} - ${FLEXI_COLUMNS_DIVIDER_WIDTH}) * ${1 - startFraction})`,
      };
    }

    const narrowColumn = resizable
      ? `${resizedStart}px`
      : `${(preset === 'narrow-wide' ? start.width : end.width) ?? 400}px`;
    const wideColumn = `calc(${full} - ${narrowColumn} - ${FLEXI_COLUMNS_DIVIDER_WIDTH})`;

    return preset === 'narrow-wide'
      ? { start: narrowColumn, end: wideColumn }
      : { start: wideColumn, end: narrowColumn };
  }, [
    clampWidth,
    defaultStartWidth,
    end.width,
    endOverlay,
    preset,
    resizable,
    start.width,
    startWidth,
    weightSum,
  ]);

  const containerCSS = useMemo(
    () => ({
      '--flexi-start-width': `${effectiveStartWidth}px`,
      '--flexi-start-fr': `${evenSplitWeight}fr`,
      '--flexi-end-fr': `${weightSum - evenSplitWeight}fr`,
      '--flexi-peek-width-start': `${start.hidden ? 0 : start.peekWidth ?? sizing.primary}px`,
      '--flexi-peek-width-end': `${end.hidden ? 0 : end.peekWidth ?? sizing.primary}px`,
      '--flexi-expanded-width-start': expandedWidths.start,
      '--flexi-expanded-width-end': expandedWidths.end,
      '--flexi-end-overlay-width': `${
        resizable ? effectiveStartWidth : end.width ?? defaultStartWidth
      }px`,
      ...(start.width != null ? { '--flexi-fixed-start': `${start.width}px` } : {}),
      ...(end.width != null ? { '--flexi-fixed-end': `${end.width}px` } : {}),
    }),
    [
      effectiveStartWidth,
      evenSplitWeight,
      expandedWidths,
      weightSum,
      start.hidden,
      end.hidden,
      start.peekWidth,
      end.peekWidth,
      end.width,
      resizable,
      defaultStartWidth,
      start.width,
    ],
  );

  const contextValue = useMemo<FlexiColumnsContextValue>(
    () => ({
      setActiveColumn,
      selectedId: selectedId ?? null,
      onDeselect,
      onNestedActiveColumnChange: handleNestedActiveColumnChange,
    }),
    [selectedId, onDeselect, handleNestedActiveColumnChange],
  );

  const startContent =
    typeof start.render === 'function'
      ? start.render({ selectedId: selectedId ?? null })
      : start.render;
  const endContent =
    typeof end.render === 'function' ? end.render({ selectedId: selectedId ?? null }) : end.render;

  return (
    <FlexiColumnsContext.Provider value={contextValue}>
      <FlexiColumnsContainer
        ref={columnsContainerRef}
        css={containerCSS}
        data-active-column={displayedColumn}
        data-both-panes-visible={showBothPanes}
        data-end-overlay={endOverlay}
        data-preset={preset}
        data-resizable={resizable}
        data-nested-active-column={nestedActiveColumn ?? undefined}
      >
        <FlexiColumnsColumn
          ref={startPaneRef}
          style={start.hidden ? { display: 'none' } : undefined}
          data-position="start"
          aria-hidden={!startPaneVisible}
          aria-expanded={startPaneVisible}
          aria-label={startPaneInteractive ? start.label : undefined}
          position="start"
          visible={startPaneVisible}
          role={startPaneInteractive ? 'button' : undefined}
          tabIndex={startPaneInteractive ? 0 : undefined}
          onClick={startPaneInteractive ? handleShowStart : undefined}
          onKeyDown={
            startPaneInteractive
              ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleShowStart();
                  }
                }
              : undefined
          }
          hasDivider={hasDivider}
        >
          <FlexiColumnsColumnBody data-flexi-slot="body">{startContent}</FlexiColumnsColumnBody>
        </FlexiColumnsColumn>
        <FlexiColumnsDivider
          data-flexi-divider
          style={hasHiddenColumn ? { display: 'none' } : undefined}
        >
          {hasControls && (
            <FlexiColumnsControls
              onCollapseStart={handleCollapseStart}
              onCollapseEnd={handleCollapseEnd}
              onResizeStart={handleResizeStart}
              onResizeKeyDown={handleResizeByKeyboard}
              onResetWidth={handleResetWidth}
              isDefaultWidth={effectiveStartWidth === defaultStartWidth}
              endLabel={end.label}
              startLabel={start.label}
              startWidth={effectiveStartWidth}
              startCollapsible={start.collapsible}
              endCollapsible={end.collapsible}
              resizable={resizable}
              resizeBounds={resizeBounds}
              resizeCursor={resizeCursor}
              isResizing={isResizing}
              containerRef={columnsContainerRef}
              autoHide={autoHideControls}
            />
          )}
        </FlexiColumnsDivider>
        <FlexiColumnsColumn
          ref={endPaneRef}
          style={end.hidden ? { display: 'none' } : undefined}
          data-position="end"
          aria-hidden={!endPaneVisible}
          aria-expanded={endPaneVisible}
          aria-label={endPaneInteractive ? end.label : undefined}
          position="end"
          visible={endPaneVisible}
          role={endPaneInteractive ? 'button' : undefined}
          tabIndex={endPaneInteractive ? 0 : undefined}
          onClick={endPaneInteractive ? handleShowEnd : undefined}
          onKeyDown={
            endPaneInteractive
              ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleShowEnd();
                  }
                }
              : undefined
          }
          hasDivider={hasDivider}
        >
          <FlexiColumnsColumnBody data-flexi-slot="body">{endContent}</FlexiColumnsColumnBody>
        </FlexiColumnsColumn>
        <FlexiColumnsOverlay
          data-flexi-overlay
          style={hasHiddenColumn ? { display: 'none' } : undefined}
        >
          <FlexiColumnsToggleButton
            data-toggle="start"
            position="start"
            opaque={!autoHideControls}
            placement="sides"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleShowStart();
            }}
            variant="neutral"
            aria-label={`Expand ${start.label.toLowerCase()}`}
            icon={start.icon ?? 'pane-start-expand'}
            label={`Expand ${start.label.toLowerCase()}`}
          />
          <FlexiColumnsToggleButton
            data-toggle="end"
            position="end"
            opaque={!autoHideControls}
            placement="sides"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleShowEnd();
            }}
            variant="neutral"
            aria-label={`Expand ${end.label.toLowerCase()}`}
            icon={end.icon ?? 'pane-end-expand'}
            label={`Expand ${end.label.toLowerCase()}`}
          />
        </FlexiColumnsOverlay>
      </FlexiColumnsContainer>
    </FlexiColumnsContext.Provider>
  );
}
